import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export interface OrderRating {
  id: string;
  order_id: string;
  shipper_rating: number | null;
  shipper_comment: string | null;
  shipper_rated_at: string | null;
  transporter_rating: number | null;
  transporter_comment: string | null;
  transporter_rated_at: string | null;
}

export interface SubmitRatingData {
  orderId: string;
  rating: number;
  comment?: string;
  ratingType: 'shipper' | 'transporter';
}

export const useOrderRating = (orderId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Fetch rating for a specific order
  const { data: rating, isLoading } = useQuery({
    queryKey: ['order-rating', orderId],
    queryFn: async () => {
      if (!orderId) return null;
      
      const { data, error } = await supabase
        .from('order_ratings')
        .select('*')
        .eq('order_id', orderId)
        .maybeSingle();

      if (error) throw error;
      return data as OrderRating | null;
    },
    enabled: !!orderId,
  });

  // Submit or update rating
  const submitRatingMutation = useMutation({
    mutationFn: async ({ orderId, rating: ratingValue, comment, ratingType }: SubmitRatingData) => {
      // Check if rating already exists
      const { data: existingRating } = await supabase
        .from('order_ratings')
        .select('*')
        .eq('order_id', orderId)
        .maybeSingle();

      const now = new Date().toISOString();
      
      if (ratingType === 'shipper') {
        const ratingData = {
          shipper_rating: ratingValue,
          shipper_comment: comment || null,
          shipper_rated_at: now,
        };

        if (existingRating) {
          // Update existing rating
          const { error } = await supabase
            .from('order_ratings')
            .update(ratingData)
            .eq('order_id', orderId);

          if (error) throw error;
        } else {
          // Insert new rating
          const { error } = await supabase
            .from('order_ratings')
            .insert({
              order_id: orderId,
              ...ratingData,
            });

          if (error) throw error;
        }
      } else {
        // Transporter rating
        const ratingData = {
          transporter_rating: ratingValue,
          transporter_comment: comment || null,
          transporter_rated_at: now,
        };

        if (existingRating) {
          const { error } = await supabase
            .from('order_ratings')
            .update(ratingData)
            .eq('order_id', orderId);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('order_ratings')
            .insert({
              order_id: orderId,
              ...ratingData,
            });

          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-rating'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['company-ratings'] });
      toast.success(t('rating.success'));
    },
    onError: (error) => {
      console.error('Error submitting rating:', error);
      toast.error(t('rating.error'));
    },
  });

  return {
    rating,
    isLoading,
    submitRating: submitRatingMutation.mutateAsync,
    isSubmitting: submitRatingMutation.isPending,
  };
};
